import { CartItem, CartItemSelection, PreviewEvent, WidgetSettings } from '@gsc/types'
import {
  IRuntime,
  IRuntimeSettings,
  IWidgetRenderer,
  IWidgetRendererFactory,
  IWidgetRuntime,
  OpenUrlParams,
  Placement,
  WidgetData,
} from '../interfaces'
import { StorageBackendType, Storages } from './storage'
import { Event, EventType, DynamicEventContext, Tracking, TrackingSubscriber } from './tracking'
import { WidgetDisplay } from './display'
import { createLocationProvider, Location } from './location'
import { Session } from './session'
import { detectPlatform } from './platforms/detect'
import Launcher from './launcher'
import { ARG_TYPES, IRuntimeArgs, RuntimeArgs } from './args'
import { executeRecaptcha } from './recaptcha'
import { ServerEventsConfig } from './tracking/server'
import { GAConfig } from './tracking/ga'
import { GTMConfig } from './tracking/gtm'
import { track as saveToStore } from './tracking/store'
import { track as runCallbacks } from './tracking/callbacks'
import { track as sparkloop } from './tracking/sparkloop'

const defaultRuntimeSettings: IRuntimeSettings = {
  sessionLength: 20,

  storage: StorageBackendType.LocalStorage,
  storagePrefix: 'gsc',

  placement: Placement.WebSite,

  events: [
    //EventType.Session,
    EventType.Show,
    EventType.SubmitWidget,
    EventType.OpenUrl,
    EventType.ShowWidget,
    EventType.CopyToClipboard,
    EventType.AddToCart,
    EventType.ApplyCoupon,
  ],

  trackLocation: true,

  previewInteractive: false,
}

export class WidgetRuntime implements IRuntime, IWidgetRuntime {
  public storages: Storages
  public settings: IRuntimeSettings

  public track: Tracking
  public display: WidgetDisplay

  public location: Location
  public session: Session

  public renderer: IWidgetRenderer

  public platform = detectPlatform()

  launcher: Launcher

  public get args(): IRuntimeArgs {
    return this._args
  }

  private _args: RuntimeArgs

  constructor(rendererFactory: IWidgetRendererFactory, cfg: Partial<IRuntimeSettings> = {}) {
    this.settings = { ...defaultRuntimeSettings, ...cfg }
    this.renderer = rendererFactory.createWidgetRenderer(this, { logoUrl: this.settings.logoUrl })
    this.location = createLocationProvider()
    this.storages = new Storages(this.settings.storage, this.settings.storagePrefix)
    this.session = new Session(this.storages)
    this.display = new WidgetDisplay(this)
    this._args = new RuntimeArgs()

    this.launcher = new Launcher()
    this.launcher.addEventListener(async ({ id, ...params }) => {
      await this.setParams(params)
      if (id) {
        await this.showWidget(id)
      }
    })
  }

  private async updateSettings(data: Partial<IRuntimeSettings> = {}) {
    if (data) {
      this.settings = { ...this.settings, ...data }
    }

    // refresh tracking
    this.initTracking()

    if (this.settings.trackLocation) {
      this.location.track(this.onLocationChange.bind(this))
    } else {
      this.location.stopTrack()
    }
  }

  private initTracking() {
    const subscribers: TrackingSubscriber[] = []

    const serverConfig = new ServerEventsConfig(this.session, this.settings.serverUrl, this.settings.events)
    if (serverConfig.isEnabled()) {
      subscribers.push(serverConfig.track)
    }

    const gaConfig = new GAConfig(this.settings.trackGA)
    if (gaConfig.isEnabled()) {
      subscribers.push(gaConfig.track)
    }

    const gtmConfig = new GTMConfig(this.settings.trackGTM)
    if (gtmConfig.isEnabled()) {
      subscribers.push(gtmConfig.track)
    }

    subscribers.push(saveToStore(this), runCallbacks(this), sparkloop())

    this.track = new Tracking(this, subscribers)
  }

  private pageView() {
    // configure session
    let sessionLoadResult = { newUser: false, newSession: false }
    if (!this.session.isInitialized()) {
      sessionLoadResult = this.session.loadOrCreate(this.location, this.settings.sessionLength)
    }
    if (this.settings.serverUrl) {
      this.session.updateServerData(this.settings.serverUrl)
    }
    this.session.pageView(this.location)

    // if (sessionLoadResult.newUser) {
    //   this.track.add(new Event(EventType.User))
    // }
    // if (sessionLoadResult.newSession) {
    //   this.track.add(new Event(EventType.Session))
    // }

    if (this.platform) {
      this.platform.pageView()
    }
    this.display.pageView()
    this.storages.page.clear()

    this.launcher.processHref(this.location().hash)
    this.track.add(new Event(EventType.PageView))
  }

  public async run(widgets: WidgetSettings[]) {
    this.updateSettings()
    this.pageView()

    await this.display.update(widgets)
  }

  public async showWidget(widgetId: number, actorId?: number): Promise<boolean> {
    if (typeof actorId !== 'undefined') {
      const widget = this.display.findWidget(widgetId)
      this.trackWidgetEvent(EventType.ShowWidget, actorId, { id: widgetId, name: widget?.name })
    }
    return this.display.showWidgetUser(widgetId)
  }

  public async closeWidget(widgetId: number): Promise<boolean> {
    return this.display.closeWidgetUser(widgetId)
  }

  public async sendData(widgetId: number, data: WidgetData): Promise<boolean> {
    const recaptcha = await this.verifyInteraction(widgetId, data)
    this.trackWidgetEvent(EventType.SubmitWidget, widgetId, data, {
      immediate: true,
      context: recaptcha ? { recaptcha } : {},
    })
    return true
  }

  openUrl(widgetId: number, { url, target, label }: OpenUrlParams): Promise<boolean> {
    this.trackWidgetEvent(EventType.OpenUrl, widgetId, { url, label }, { immediate: target !== '_blank' })
    window.open(url, target)
    return Promise.resolve(true)
  }

  copyToClipboard(widgetId: number, text: string): Promise<boolean> {
    if (!navigator.clipboard) {
      return Promise.resolve(false)
    }

    this.trackWidgetEvent(EventType.CopyToClipboard, widgetId, { text })
    return navigator.clipboard.writeText(text).then(() => true)
  }

  addToCart(widgetId: number, selection: CartItemSelection, items: CartItem[], redirect: string): Promise<boolean> {
    if (this.platform) {
      const _exec = (cartItems: CartItem[]): Promise<boolean> => {
        const eventData = {
          items: cartItems.map(i => {
            return { id: i.id, quantity: i.quantity, name: i.label }
          }),
        }
        this.trackWidgetEvent(EventType.AddToCart, widgetId, eventData)
        return this.platform.runAddToCart(cartItems, redirect).then(() => true)
      }

      if (selection == CartItemSelection.Current) {
        return this.platform.getCurrentItem().then(item => {
          if (item) {
            return _exec([item])
          } else {
            console.error('current cart item not found')
            return false
          }
        })
      } else {
        return _exec(items)
      }
    } else {
      console.error('add to cart not implemented')
    }

    return Promise.resolve(false)
  }

  applyCoupon(widgetId: number, coupon: string, redirect: string): Promise<boolean> {
    if (this.platform) {
      this.trackWidgetEvent(EventType.ApplyCoupon, widgetId, { coupon })
      return this.platform.runApplyCoupon(coupon, redirect)
    } else {
      console.error('add to cart not implemented')
      return Promise.resolve(false)
    }
  }

  private trackWidgetEvent(
    type: EventType,
    widgetId: number,
    data: any = {},
    options: { immediate?: boolean; context?: DynamicEventContext } = {},
  ) {
    const widget = this.display.findWidget(widgetId)
    this.track.add(new Event(type, widget, data), options)
  }

  private async verifyInteraction(
    widgetId: number,
    data: WidgetData,
  ): Promise<{ token: string; serverKey: string } | null> {
    const widget = this.display.findWidget(widgetId)
    const { recaptcha } = this.settings
    const isEmpty = Object.keys(data).length === 0

    if (!widget.settings.recaptcha || !recaptcha || isEmpty) {
      return null
    }

    try {
      const token = await executeRecaptcha(recaptcha, { hideBadge: widget.settings.recaptcha_hide_badge })
      return { token, serverKey: recaptcha.serverKey }
    } catch (err) {
      console.error('recaptcha error', err)
      return null
    }
  }

  public sendPreviewEvent(evt: PreviewEvent): Promise<boolean> {
    return Promise.resolve(false)
  }

  public async apiCall(input: any) {
    const arg = this._args.addNew(input)

    let updateRequired = false
    let response = null
    console.log('new arg ', arg)

    if (arg.type == ARG_TYPES.Settings) {
      this.updateSettings(arg.value)
    } else if (arg.type == ARG_TYPES.Params) {
      this.display.updateWidgetTags(this.args.getParams())
      updateRequired = true
    } else if (arg.type == ARG_TYPES.TrackPage) {
      this.location.set(arg.value)
      this.pageView()
      updateRequired = true
    } else if (arg.type == ARG_TYPES.ShowWidget) {
      response = await this.showWidget(parseInt(arg.value))
    } else if (arg.type == ARG_TYPES.CloseWidget) {
      response = await this.closeWidget(parseInt(arg.value))
    } else if (arg.type == ARG_TYPES.HideWidget) {
      response = await this.display.hideWidget(parseInt(arg.value))
    }

    // finalize
    if (updateRequired) {
      await this.display.update()
    }

    return response
  }

  public async onLocationChange() {
    this.pageView()
    await this.display.update()
  }

  private async setParams(params: { [key: string]: any }) {
    if (params && Object.keys(params).length > 0) {
      await this.display.updateWidgetTags(params)
      await this.display.update()
    }
  }
}
